// Notification sound generator using Web Audio API. No asset file required —
// the chime is synthesised live so it works out of the box on every browser.
//
// To swap the default sound, change DEFAULT_SOUND below to one of:
//   'pleasant' — soft 2-tone rising chime (Slack-like)        ← default
//   'ding'     — single bright bell ding
//   'soft'     — gentle low pulse (less attention-grabbing)
//   'double'   — quick double-tap (matches WhatsApp vibe)
//
// All chimes are short (<400ms) and tuned to be pleasant — nothing harsh.
// IMPORTANT: this only controls the FOREGROUND sound. When the page is
// closed, the OS plays its own default notification sound — that's a
// browser-level restriction we can't bypass.

const DEFAULT_SOUND = 'pleasant';

let _ctx = null;
const getAudioContext = () => {
  if (_ctx) return _ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  _ctx = new Ctx();
  return _ctx;
};

// Internal: schedule a single tone with soft attack + exponential decay
// so it sounds like a real chime, not a buzz.
const scheduleTone = (ctx, { freq, start = 0, duration = 0.2, peak = 0.18, type = 'sine' }) => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const t0 = ctx.currentTime + start;
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.015);          // 15ms attack
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);     // exponential tail
  osc.connect(gain).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.05);
};

const PRESETS = {
  // Soft 2-tone rising — most "professional" feel.
  pleasant: (ctx) => {
    scheduleTone(ctx, { freq: 783.99, start: 0,    duration: 0.20 }); // G5
    scheduleTone(ctx, { freq: 1046.5, start: 0.10, duration: 0.30 }); // C6
  },
  // Bright single bell — short and snappy.
  ding: (ctx) => {
    scheduleTone(ctx, { freq: 1318.5, start: 0, duration: 0.45, peak: 0.22 });
  },
  // Low warm pulse — least intrusive, good for high-volume admin alerts.
  soft: (ctx) => {
    scheduleTone(ctx, { freq: 523.25, start: 0,    duration: 0.18, peak: 0.14 }); // C5
    scheduleTone(ctx, { freq: 659.25, start: 0.06, duration: 0.22, peak: 0.14 }); // E5
  },
  // Quick double-tap — WhatsApp / Telegram vibe.
  double: (ctx) => {
    scheduleTone(ctx, { freq: 1108.7, start: 0,     duration: 0.10 });
    scheduleTone(ctx, { freq: 1108.7, start: 0.13,  duration: 0.10 });
  },
};

// Play the configured (or named) preset. Safe to call from anywhere —
// silently no-ops if AudioContext isn't supported (very old browsers).
export const playNotificationSound = (preset = DEFAULT_SOUND) => {
  const ctx = getAudioContext();
  if (!ctx) return;
  // Browsers suspend AudioContext until the user has interacted with the
  // page. resume() is best-effort; if it's still suspended (e.g. tab never
  // got focus), the sound is dropped.
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  try {
    (PRESETS[preset] || PRESETS[DEFAULT_SOUND])(ctx);
  } catch { /* AudioContext can throw in iframes / restricted contexts */ }
};

// Exposed for components that want to offer a "Try sound" preview button.
export const SOUND_PRESETS = Object.keys(PRESETS);
